import { useEffect, useMemo, useRef, useState } from "react";
import { fetchSessionKpis, SessionKpi } from "../lib/api";
import { getDeviceScope, scopeToDeviceIdParam } from "../lib/scope";
import {
  fetchSessionCoverage,
  fetchSessionPerimeter,
  fetchTrialPath,
  pathCutoffIndex,
  sqmToAcres,
  type CoverageResponse,
  type GpsPoint,
  type PerimeterPoint,
} from "../lib/sessionDetail";
import { useSessionPlayback } from "../hooks/useSessionPlayback";
import { SessionPathMap } from "../components/SessionPathMap";
import { SessionScrubber } from "../components/SessionScrubber";
import { SessionVideoPanel } from "../components/SessionVideoPanel";
import { isLiveSession } from "../lib/sessionVideos";
import { LiveTelemetryPanel } from "../components/LiveTelemetryPanel";
import { useDashboardSocket, useLiveFleet } from "../live/DashboardSocketContext";
import { liveSessionsFromFleet } from "../lib/liveFrames";
import { useLiveDataStale, useLiveTelemetryPath } from "../hooks/useLiveTelemetryPath";

type OutcomeBadge = "success" | "warning" | "danger" | "info";

const BC: Record<OutcomeBadge, string> = {
  success: "bgs",
  warning: "bgw",
  danger: "bgd",
  info: "bgi",
};
const BL: Record<OutcomeBadge, string> = {
  success: "Complete",
  warning: "Incomplete",
  danger: "Failed",
  info: "In progress",
};

function badgeFor(s: SessionKpi): OutcomeBadge {
  // Check live first: a running session has no outcome yet, and without this it
  // would read as "Incomplete" — which is a verdict, not a pending state.
  if (isLiveSession(s.status)) return "info";
  if (s.session_outcome === "COMPLETE") return "success";
  if (s.session_outcome === "FAULT") return "danger";
  return "warning";
}

/** Wall-clock seconds since a running session started. A live row's
 *  `active_session_seconds` is 0 (KPIs are computed at finalisation), so
 *  elapsed time is the only duration there is to show — and it is explicitly
 *  elapsed, not the active-mowing measure the finished rows report. */
function elapsedSeconds(s: SessionKpi): number {
  if (!s.start_time) return 0;
  const started = new Date(s.start_time).getTime();
  if (Number.isNaN(started)) return 0;
  return Math.max(0, Math.round((Date.now() - started) / 1000));
}

function durationLabel(seconds: number): string {
  if (!seconds || seconds < 60) return `${Math.round(seconds || 0)} sec`;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m}m`;
}

function formatStart(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function coveragePct(s: SessionKpi): number {
  const active = s.active_session_seconds || 0;
  if (active === 0) return 0;
  const mowing = s.active_mowing_seconds || 0;
  return Math.min(100, Math.round((mowing / active) * 100));
}

export function SessionsPage({
  startDate,
  endDate,
  initialMower,
}: {
  startDate?: string;
  endDate?: string;
  /** Pre-select a mower filter (e.g. when arriving from the Fleet screen). */
  initialMower?: string;
}) {
  const [sessions, setSessions] = useState<SessionKpi[]>([]);
  const [selected, setSelected] = useState<SessionKpi | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Local filters. Dates are seeded from the app-wide default range but the
  // user can narrow them here; they drive the server-side fetch. Mower is a
  // client-side filter over the fetched (date- and scope-limited) sessions.
  const [fromDate, setFromDate] = useState<string>(startDate ?? "");
  const [toDate, setToDate] = useState<string>(endDate ?? "");
  const [mower, setMower] = useState<string>(initialMower ?? "all");

  const { sessionRefetch } = useDashboardSocket();
  const fleet = useLiveFleet();
  // `null` = internal staff (unrestricted); an array = the customer's device
  // pool. Held so live rows are scoped by the same rule as the fetch.
  const [deviceScope, setDeviceScope] = useState<string[] | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // A session starting or ending changes the durable rows — refetch instead of
  // trying to synthesise the new row from the delta, which carries no KPIs.
  useEffect(() => {
    if (sessionRefetch.seq === 0) return;
    setReloadKey((k) => k + 1);
  }, [sessionRefetch]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    (async () => {
      try {
        const scope = await getDeviceScope();
        if (!cancelled) setDeviceScope(scope);
        // External customer with no devices in their company pool → show
        // nothing rather than querying the unscoped (whole-fleet) endpoint.
        if (scope !== null && scope.length === 0) {
          if (!cancelled) setSessions([]);
          return;
        }
        const deviceId = scopeToDeviceIdParam(scope);
        const s = await fetchSessionKpis({
          startDate: fromDate || undefined,
          endDate: toDate || undefined,
          limit: 100,
          deviceId,
        });
        if (!cancelled) setSessions(s);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fromDate, toDate, reloadKey]);

  // Running sessions come from the socket, not the fetch: /kpis/sessions joins
  // through SessionKPIs, which aren't written until a session finalises, so a
  // live run is invisible to it. The snapshot names the device's current trial,
  // which is enough to stand up a row — with no KPIs on it, because none exist
  // yet. Each is a pointer to a session in flight, not a measurement of one.
  const liveRows = useMemo<SessionKpi[]>(() => {
    return liveSessionsFromFleet(fleet)
      .filter((l) => mower === "all" || l.device_id === mower)
      // Same scope rule as the fetch. The server already authorization-filters
      // the snapshot per connection, so this is defense in depth — but it also
      // keeps Command's customer-pool scoping (which is its own notion, not the
      // server's) applied uniformly to both sources.
      .filter((l) => deviceScope === null || deviceScope.includes(l.device_id))
      .map((l) => ({
        trial_number: l.trial_number,
        device_id: l.device_id,
        total_stops: 0,
        error_stops_total: 0,
        total_pause_seconds: 0,
        stops_by_category: {},
        pause_seconds_by_category: {},
        session_outcome: null,
        active_session_seconds: 0,
        active_mowing_seconds: 0,
        rerouting_seconds: 0,
        pause_seconds: 0,
        idle_seconds: 0,
        area_mowed_sqm: 0,
        area_mowed_acres: 0,
        productivity_acres_per_hour: 0,
        start_time: l.start_time ?? "",
        status: "in_progress",
        software_version: "",
        address: null,
        interventions: 0,
        acres_per_intervention: null,
        hours_per_intervention: null,
      }));
  }, [fleet, mower, deviceScope]);

  const sorted = useMemo(() => {
    // A session that just ended appears in both for one refetch — the socket
    // hasn't cleared session_status yet while SQL already has the finished row.
    // The fetched row wins: it carries real KPIs.
    const fetched = new Set(
      sessions.map((s) => `${s.device_id}|${s.trial_number}`),
    );
    const live = liveRows.filter(
      (l) => !fetched.has(`${l.device_id}|${l.trial_number}`),
    );
    return [
      // Running sessions first: they're the only rows still actionable, and by
      // start_time alone a long-running one sinks below newer finished ones.
      ...live.sort((a, b) =>
        (b.start_time || "").localeCompare(a.start_time || ""),
      ),
      ...[...sessions].sort((a, b) =>
        (b.start_time || "").localeCompare(a.start_time || ""),
      ),
    ];
  }, [sessions, liveRows]);

  // Mower dropdown options: every device that appears in the fetched window.
  const mowerOptions = useMemo(
    () =>
      Array.from(new Set(sessions.map((s) => s.device_id))).sort((a, b) =>
        a.localeCompare(b),
      ),
    [sessions],
  );

  const visible = useMemo(
    () => (mower === "all" ? sorted : sorted.filter((s) => s.device_id === mower)),
    [sorted, mower],
  );

  if (loading)
    return <p style={{ color: "var(--color-text-secondary)" }}>Loading sessions…</p>;
  if (error)
    return (
      <p style={{ color: "var(--color-text-danger)" }}>
        Couldn't load sessions: {error}
      </p>
    );

  if (selected) {
    return <SessionDetail session={selected} onBack={() => setSelected(null)} />;
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 10 }}>
        <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", color: "var(--color-text-secondary)", margin: 0 }}>
          SESSIONS — {visible.length} {mower === "all" ? "TOTAL" : "SHOWN"}
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <label className="ss-filter">
            <span>From</span>
            <input
              type="date"
              value={fromDate}
              max={toDate || undefined}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </label>
          <label className="ss-filter">
            <span>To</span>
            <input
              type="date"
              value={toDate}
              min={fromDate || undefined}
              onChange={(e) => setToDate(e.target.value)}
            />
          </label>
          <label className="ss-filter">
            <span>Mower</span>
            <select value={mower} onChange={(e) => setMower(e.target.value)}>
              <option value="all">All mowers</option>
              {/* Keep the active selection listed even if it has no sessions
                  in the current window, so the filter doesn't silently reset. */}
              {(mowerOptions.includes(mower) || mower === "all"
                ? mowerOptions
                : [mower, ...mowerOptions]
              ).map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
      {visible.length === 0 && (
        <p style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>
          {mower === "all"
            ? "No sessions in this period."
            : "No sessions for this mower in this period."}
        </p>
      )}
      {visible.map((s) => {
        const cov = coveragePct(s);
        const cc = cov >= 80 ? "#22c55e" : cov >= 40 ? "#5E85C4" : "#f59e0b";
        const badge = badgeFor(s);
        return (
          <div
            key={`${s.device_id}-${s.trial_number}`}
            className="sr"
            onClick={() => setSelected(s)}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "var(--color-text-primary)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {s.device_id} — {s.address || "Unknown location"}
              </div>
              <div style={{ fontSize: 10, color: "var(--color-text-secondary)", marginTop: 2 }}>
                {s.start_time ? formatStart(s.start_time) : "—"} ·{" "}
                {isLiveSession(s.status)
                  ? `${durationLabel(elapsedSeconds(s))} elapsed`
                  : `${durationLabel(s.active_session_seconds)} · ${s.area_mowed_acres.toFixed(2)} ac`}
              </div>
            </div>
            <span className={"bg " + BC[badge]}>{BL[badge]}</span>
            <div style={{ textAlign: "right", minWidth: 52 }}>
              {isLiveSession(s.status) ? (
                // No coverage number for a running session — the KPI doesn't
                // exist yet, and 0% would read as a measurement of failure.
                <div style={{ fontSize: 10, color: "var(--color-text-secondary)" }}>
                  in progress
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 15, fontWeight: 700, fontFamily: "'Roboto Slab', Georgia, serif", color: cc }}>
                    {cov}%
                  </div>
                  <div style={{ fontSize: 10, color: "var(--color-text-secondary)" }}>coverage</div>
                </>
              )}
            </div>
            <i className="ti ti-chevron-right" style={{ color: "var(--color-text-tertiary)" }} aria-hidden="true" />
          </div>
        );
      })}
    </div>
  );
}

function SessionDetail({ session, onBack }: { session: SessionKpi; onBack: () => void }) {
  const cov = coveragePct(session);
  const cc = cov >= 80 ? "#22c55e" : cov >= 40 ? "#5E85C4" : "#f59e0b";
  const badge = badgeFor(session);

  // --- Path / coverage / scrubber data -------------------------------------
  const [restPath, setRestPath] = useState<GpsPoint[]>([]);
  const [perimeter, setPerimeter] = useState<PerimeterPoint[]>([]);
  const [coverage, setCoverage] = useState<CoverageResponse | null>(null);
  const [pathLoading, setPathLoading] = useState(true);
  const [pathError, setPathError] = useState<string | null>(null);
  const [primed, setPrimed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPathLoading(true);
    setPathError(null);
    setRestPath([]);
    setPerimeter([]);
    setCoverage(null);
    setPrimed(false);
    (async () => {
      try {
        const [p, peri] = await Promise.all([
          fetchTrialPath(session.trial_number, session.device_id),
          fetchSessionPerimeter(session.trial_number, session.device_id).catch(() => []),
        ]);
        if (cancelled) return;
        setRestPath(p);
        setPerimeter(peri);
      } catch (e) {
        if (!cancelled) setPathError((e as Error).message);
      } finally {
        if (!cancelled) setPathLoading(false);
      }
      // Coverage is server-rasterised and slower; load it separately so the
      // map/path show immediately and a coverage failure doesn't block them.
      try {
        const c = await fetchSessionCoverage(session.trial_number, session.device_id);
        if (!cancelled) setCoverage(c);
      } catch {
        /* coverage is optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session.trial_number, session.device_id]);

  const live = isLiveSession(session.status);
  const liveTelemetry = useLiveTelemetryPath(session.device_id, live);
  const isStale = useLiveDataStale(live, liveTelemetry.path.length);
  const { connected } = useDashboardSocket();

  // The REST load is the trunk; live frames extend it. Points that arrived
  // before the REST snapshot are dropped by timestamp rather than concatenated,
  // so a slow initial load can't double-draw the overlap.
  const path = useMemo(() => {
    if (!live || liveTelemetry.path.length === 0) return restPath;
    if (restPath.length === 0) return liveTelemetry.path;
    const lastRest = restPath[restPath.length - 1].timestamp;
    return [
      ...restPath,
      ...liveTelemetry.path.filter((p) => p.timestamp > lastRest),
    ];
  }, [restPath, liveTelemetry.path, live]);

  const startMs = path.length
    ? path[0].timestamp
    : session.start_time
    ? new Date(session.start_time).getTime()
    : 0;
  const endMs = path.length ? path[path.length - 1].timestamp : startMs;
  const totalSeconds = Math.max(0, Math.round((endMs - startMs) / 1000));

  const playback = useSessionPlayback(totalSeconds);
  const { setIndex } = playback;

  // Start with the whole path shown (scrubber parked at the end); pressing play
  // restarts the animation from the beginning.
  useEffect(() => {
    if (!primed && totalSeconds > 0) {
      setIndex(totalSeconds);
      setPrimed(true);
    }
  }, [primed, totalSeconds, setIndex]);

  // While live, keep the scrubber pinned to the newest point as the session
  // grows — but only if it was already sitting at the head. Once the user
  // scrubs back or hits play, they own the position and new frames must not
  // yank the view forward underneath them.
  const prevTotalRef = useRef(totalSeconds);
  useEffect(() => {
    const prevTotal = prevTotalRef.current;
    prevTotalRef.current = totalSeconds;
    if (!live || !primed || totalSeconds <= prevTotal) return;
    if (playback.isPlaying) return;
    if (playback.index >= prevTotal) setIndex(totalSeconds);
  }, [live, primed, totalSeconds, playback.isPlaying, playback.index, setIndex]);

  const currentMs = startMs + playback.index * 1000;
  const visiblePath = useMemo(() => {
    if (path.length === 0) return [];
    const cut = pathCutoffIndex(path, currentMs);
    return cut < 0 ? path.slice(0, 1) : path.slice(0, cut + 1);
  }, [path, currentMs]);

  const covStats = coverage?.stats;

  return (
    <>
      <button className="bbtn" onClick={onBack}>
        <i className="ti ti-arrow-left" style={{ marginRight: 4 }} aria-hidden="true" />
        ALL SESSIONS
      </button>
      <div className="dp">
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
          <div>
            <p style={{ fontSize: 14, fontWeight: 700, margin: "0 0 3px", color: "var(--color-text-primary)" }}>
              {session.device_id} — {session.address || "Unknown location"}
            </p>
            <p style={{ fontSize: 11, color: "var(--color-text-secondary)", margin: 0 }}>
              {session.start_time ? formatStart(session.start_time) : "—"}
            </p>
          </div>
          <span className={"bg " + BC[badge]}>{BL[badge]}</span>
        </div>
        {/* When spatial coverage stats are available (shown in the Path &
            Coverage card below), they supersede the time-based area/coverage
            here — so drop those two to avoid redundant, conflicting numbers. */}
        <div className={covStats ? "dk3" : "dk5"}>
          {!covStats && (
            <>
              <DetailKpi label="AREA COVERED" value={session.area_mowed_acres.toFixed(2)} unit=" ac" />
              <DetailKpi label="COVERAGE %" value={String(cov)} unit="%" color={cc} sub="active vs. mowing" />
            </>
          )}
          <DetailKpi
            label={live ? "ELAPSED" : "DURATION"}
            value={durationLabel(
              // active_session_seconds is written at finalisation, so it reads
              // 0 for a running session. Prefer the robot's own mowed_duration
              // when the stream has reported it, else wall-clock since start.
              live
                ? liveTelemetry.reading?.mowed_duration ?? elapsedSeconds(session)
                : session.active_session_seconds,
            )}
            sub={live ? "so far" : undefined}
          />
          <DetailKpi label="AUTO-PAUSES" value={String(session.total_stops - session.error_stops_total)} sub="self-resolved" />
          <DetailKpi
            label="INTERVENTIONS"
            value={String(session.interventions || 0)}
            color={session.interventions > 0 ? "#f59e0b" : undefined}
            sub="human assists"
          />
        </div>
        <div style={{ marginTop: 12 }}>
          <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", color: "var(--color-text-secondary)", margin: "0 0 6px" }}>
            STOPS BY CATEGORY
          </p>
          {Object.keys(session.stops_by_category || {}).length === 0 ? (
            <p style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>No stops recorded.</p>
          ) : (
            Object.entries(session.stops_by_category).map(([cat, count]) => (
              <div className="ev" key={cat}>
                <span style={{ color: "var(--color-text-tertiary)", minWidth: 30, flexShrink: 0, fontWeight: 700 }}>
                  {count}
                </span>
                <span style={{ color: "var(--color-text-secondary)" }}>{cat}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {live && (
        <LiveTelemetryPanel
          reading={liveTelemetry.reading}
          frameCount={liveTelemetry.path.length}
          connected={connected}
          stale={isStale}
          lastFrameMs={liveTelemetry.lastFrameMs}
        />
      )}

      <div className="dp" style={{ marginTop: 8 }}>
        <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", color: "var(--color-text-secondary)", margin: "0 0 8px" }}>
          PATH &amp; COVERAGE
        </p>
        {pathLoading ? (
          <p style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>Loading path…</p>
        ) : pathError ? (
          <p style={{ fontSize: 11, color: "var(--color-text-danger)" }}>Couldn't load path: {pathError}</p>
        ) : path.length === 0 ? (
          <p style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>
            {live
              ? "Waiting for the first live position from this mower…"
              : "No GPS path recorded for this session."}
          </p>
        ) : (
          <div className="session-detail-cols" data-cols={covStats ? "2" : "1"}>
            <div className="session-detail-map">
              <SessionPathMap
                fullPath={path}
                visiblePath={visiblePath}
                perimeter={perimeter}
                coverageBase64={coverage?.coverageMapBase64 ?? null}
              />
              <SessionScrubber
                index={playback.index}
                maxIndex={playback.maxIndex}
                isPlaying={playback.isPlaying}
                onToggle={playback.togglePlay}
                onScrub={playback.setIndex}
                onSkip={playback.skip}
                speed={playback.speed}
                onSpeed={playback.setSpeed}
                startMs={startMs}
              />
            </div>
            {covStats && (
              <div className="session-detail-stats">
                <DetailKpi
                  label="COVERED"
                  value={sqmToAcres(covStats.coveredArea).toFixed(2)}
                  unit=" ac"
                  sub={`${covStats.coveragePercent.toFixed(0)}% of area`}
                />
                <DetailKpi
                  label="MISSED"
                  value={sqmToAcres(covStats.missedArea).toFixed(2)}
                  unit=" ac"
                  sub={covStats.gapCount ? `${covStats.gapCount} gaps` : "no gaps"}
                />
                <DetailKpi
                  label="OVERLAP"
                  value={covStats.overlapPercent.toFixed(0)}
                  unit="%"
                  sub="double-covered"
                />
                <DetailKpi
                  label="TOTAL AREA"
                  value={sqmToAcres(covStats.totalArea).toFixed(2)}
                  unit=" ac"
                  sub="within boundary"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Videos are encoded when a session finalises, so a live session has
          nothing to play — skip the card rather than showing an empty one. */}
      {!isLiveSession(session.status) && (
        <SessionVideoPanel
          trialNumber={session.trial_number}
          deviceId={session.device_id}
        />
      )}
    </>
  );
}

function DetailKpi({
  label,
  value,
  unit,
  sub,
  color,
}: {
  label: string;
  value: string;
  unit?: string;
  sub?: string;
  color?: string;
}) {
  return (
    <div className="kc" style={{ padding: "10px 12px" }}>
      <p className="kl">{label}</p>
      <p className="kv" style={{ fontSize: 17, color }}>
        {value}
        {unit && <span style={{ fontSize: 12 }}>{unit}</span>}
      </p>
      {sub && <p className="ks">{sub}</p>}
    </div>
  );
}
